import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { setupManagementUrl, reportsManagementUrl } from "../../../config";
import { generateReport } from "../../../data/DynamicTable/DynamicFunctions";
import { tokens } from "../../../theme";
import { AllProducts } from "../../../data/Axios/queries";
import { graphqlQuery } from "../../../data/Axios/DynamicService";
import BusinessSummaryReport from "./businesssummary";

const ReportCards = () => {
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken") || "{}");
  const branchId = parseInt(decodedToken?.BranchId) || null;

  const [isLoading, setIsLoading] = useState(false);
  const [startDate, setStartDate] = useState(
    dayjs().subtract(7, "days").format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(dayjs().format("YYYY-MM-DD"));
  const [productsData, setProductsData] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    async function fetchAllData() {
      try {
        const productResponse = await graphqlQuery(
          AllProducts,
          setupManagementUrl.uri
        );
        setProductsData(productResponse || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    }

    fetchAllData();
  }, []);

  const handleGenerateReportWithTime = async (reportTypeID, ReportType) => {
    try {
      let reportStartDate = startDate;
      let reportEndDate = endDate;

      const mutationData = {
        ReportTypeId: String(reportTypeID),
        ReportType: ReportType,
        StartDate: reportStartDate,
        EndDate: reportEndDate,
        BranchId: branchId || null,
        UserId: null,
      };

      if (reportTypeID === 99) {
        mutationData.ProductId = selectedProduct;
      }

      const newTab = window.open("", "_blank");
      newTab.document.write(`
        <html>
          <head><title>Loading...</title></head>
          <body><div>Loading, please wait...</div></body>
        </html>
      `);
      newTab.document.close();

      const response = await generateReport(
        mutationData,
        reportsManagementUrl.generate_report_uri,
        setIsLoading
      );

      if (response.ok) {
        const htmlContent = await response.text();
        newTab.document.open();
        newTab.document.write(htmlContent);
        newTab.document.close();
      } else {
        newTab.close();
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: "An error occurred while processing your request.",
      });
    } finally {
      setIsLoading(false);
      if (reportTypeID === 99) {
        setSelectedProduct(""); // Reset selected product after generating the report
      }
    }
  };

  return (
    <>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: "bold", textAlign: "center" }}>
        Quick Downloads
      </Typography>


      <Box display="grid" gridTemplateColumns={isMobile ? "repeat(1, 1fr)" : "repeat(3, 1fr)"} gap={2} mt={4}>
        {/* Business Summary Report Card */}
        <BusinessSummaryReport />
        {[
          { title: "Get All Users Report", id: 1 },
          { title: "Customers Report", id: 2 },
          { title: "Shift Report", id: 5, time: true },
          { title: "Sales Report", id: 3, time: true },
          { title: "Purchases Report", id: 4, time: true },
          { title: "Profit & Loss Report", id: 6, time: true },
          { title: "Income Statement Report", id: 7, time: true },
          { title: "Product Statement Report", id: 99, time: true },
          { title: "Sale Order Items Credit Report", id: 1020, time: true },
          { title: "Shift Report Per User", id: 1019, time: true },
          { title: "Retail Inventory Report", id: 1023, time: true },
          { title: "Stock Approaching Due Report", id: 1021, time: true },
          { title: "Stock Past Due Report", id: 1022, time: true },
        ].map(({ title, id, time }) => (
          <Card
            key={id}
            sx={{
              padding: "16px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              borderRadius: "8px",
              transition: "transform 0.3s ease-in-out",
              "&:hover": { transform: "scale(1.05)" },
              background: `${colors.primary[400]} !important`,
            }}
          >

            <Typography variant="h6" gutterBottom sx={{ fontWeight: "bold" }}>
              {title}
            </Typography>
            {time && (
              <>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  sx={{ marginBottom: 2, width: "100%" }}
                />
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  sx={{ marginBottom: 2, width: "100%" }}
                />
              </>
            )}
            {id === 99 && (
              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel>Select Product</InputLabel>
                <Select
                  value={selectedProduct}
                  onChange={(e) => setSelectedProduct(e.target.value)}
                  label="Select Product"
                >
                  {productsData.map((product) => (
                    <MenuItem key={product.id} value={product.id}>
                      {product.productName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleGenerateReportWithTime(id, title)}
            >
              Generate Report
            </Button>
          </Card>
        ))}


        {isLoading && <CircularProgress />}
      </Box>
    </>
  );
};

export default ReportCards;